import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { selectionRelease, useApp, useSession, useUi, useWindow } from '../app/context';
import { patchUi } from '../app/ui';
import { toggleInline } from '../editor/markdownCommands';
import { BoldIcon, CodeIcon, ItalicIcon, LinkIcon, StrikeIcon } from './icons';
import { shortcutLabel } from './keys';
import { LinkField, Tool, toolbarKeys, useCommand, useLamp } from './pillParts';

/**
 * The selection pill: a smaller sibling of the primary pill that offers only
 * what applies to the selected words, right where they are. It appears once a
 * selection settles, glides as the selection changes, steps aside for
 * scrolling, and never takes focus or the selection away from the text.
 *
 * Where: at the point where the selection was finished — the pointer's
 * release for a mouse, the caret for the keyboard — on the side of that line
 * that is not selected, so it never covers the words it acts on. On touch it
 * sits below the selection, clear of the system's handles and callout.
 */

const SETTLE_MS = 260;
const GAP = 10;
const EDGE = 8;
const coarse = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;

export function SelectionPill({ objectRef }: { objectRef: RefObject<HTMLElement | null> }) {
  const app = useApp();
  const activeId = useWindow((w) => w.activeId);
  const isMd = useSession((s) => s.docs.find((d) => d.id === activeId)?.kind === 'md');
  const tick = useUi((s) => s.selectionTick);
  const empty = useUi((s) => s.selectionEmpty);
  const dragging = useUi((s) => s.dragging);
  const linkEditor = useUi((s) => s.linkEditor);
  const format = useUi((s) => s.format);
  const dialog = useUi((s) => s.dialog);
  const run = useCommand();

  const ref = useRef<HTMLDivElement>(null);
  const lamp = useRef<HTMLDivElement>(null);
  useLamp(ref, lamp);

  const [pos, setPos] = useState<{ x: number; y: number; below: boolean } | null>(null);
  const [settled, setSettled] = useState(false);
  const [dismissedAt, setDismissedAt] = useState<number | null>(null);
  const [focusTick, setFocusTick] = useState(0);
  const linking = linkEditor === 'context';
  const wanted = isMd && !empty && !dragging && !dialog && dismissedAt !== tick;

  // Wait for the selection to settle before appearing; once visible, follow it immediately.
  const [prev, setPrev] = useState({ tick, dragging });
  if (tick !== prev.tick || dragging !== prev.dragging) {
    setPrev({ tick, dragging });
    // A finished pointer drag is already a settled selection; keyboard selection waits a beat.
    if (prev.dragging && !dragging) setSettled(true);
    else if (!pos && settled && tick !== prev.tick) setSettled(false);
  }
  useEffect(() => {
    if (!wanted || settled) return;
    const t = setTimeout(() => setSettled(true), SETTLE_MS);
    return () => clearTimeout(t);
  }, [wanted, settled, tick]);

  // Re-evaluate when focus moves in or out of the editor.
  useEffect(() => {
    const bump = () => setFocusTick((n) => n + 1);
    document.addEventListener('focusin', bump);
    document.addEventListener('focusout', bump);
    return () => {
      document.removeEventListener('focusin', bump);
      document.removeEventListener('focusout', bump);
    };
  }, []);

  // Escape inside the editor puts the pill away until the selection changes.
  useEffect(() => {
    const view = app.view();
    if (!view) return;
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && pos) {
        setDismissedAt(tick);
        e.preventDefault();
      }
    };
    view.contentDOM.addEventListener('keydown', key);
    return () => view.contentDOM.removeEventListener('keydown', key);
  }, [app, pos, tick]);

  useLayoutEffect(() => {
    const view = app.view();
    const object = objectRef.current;
    const el = ref.current;
    const inside = el?.contains(document.activeElement) ?? false;
    if (!view || !object || !(wanted || linking) || !(settled || linking) || !(view.hasFocus || inside || linking)) {
      setPos(null);
      return;
    }
    let frame = 0;
    const place = () => {
      const sel = view.state.selection.main;
      const { from, to, head } = sel;
      const box = object.getBoundingClientRect();
      const scroller = view.scrollDOM.getBoundingClientRect();
      const width = el?.offsetWidth ?? 180;
      const height = el?.offsetHeight ?? 36;
      const top = scroller.top + 4;
      const bottom = scroller.bottom - 84; // stay clear of the primary pill
      const end = coarse ? view.coordsAtPos(to, -1) : view.coordsAtPos(head, head === to ? -1 : 1);
      const start = view.coordsAtPos(from, 1);
      if (!end || !start) return setPos(null);
      const anchorLine = end;
      if (anchorLine.bottom < top || anchorLine.top > bottom) return setPos(null);

      // Horizontally: where the pointer let go (if this selection was made by pointer), else the caret.
      const release = selectionRelease.get();
      const byPointer = !coarse && !!release && release.view === view && (release.head === head || Math.abs(release.y - (end.top + end.bottom) / 2) < 40);
      const anchorX = byPointer ? release!.x : anchorLine.left;

      // Vertically: on the unselected side of the line where the selection ended.
      const multiline = Math.abs(start.top - end.top) > 4;
      const forward = head === to;
      let below = coarse || (multiline && forward);
      const above = (line: { top: number }) => line.top - GAP - height;
      const under = (line: { bottom: number }) => line.bottom + GAP + (coarse ? 22 : 0);
      let y = below ? under(anchorLine) : above(anchorLine);
      if (!below && y < top) {
        below = true;
        y = under(anchorLine);
      } else if (below && y + height > bottom + 40) {
        below = false;
        y = Math.max(top, above(anchorLine));
      }
      const x = Math.min(box.right - EDGE - width, Math.max(box.left + EDGE, anchorX - width / 2));
      setPos({ x: x - box.left, y: y - box.top, below });
    };
    place();
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(place);
    };
    view.scrollDOM.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      cancelAnimationFrame(frame);
      view.scrollDOM.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, [app, objectRef, wanted, settled, linking, tick, focusTick]);

  useEffect(() => {
    patchUi({ contextShown: !!pos });
  }, [pos]);

  if (!pos) return null;

  return (
    <div
      ref={ref}
      className="selection-pill"
      role="toolbar"
      aria-label="Format selection"
      data-below={pos.below || undefined}
      data-linking={linking || undefined}
      style={{ transform: `translate3d(${Math.round(pos.x)}px, ${Math.round(pos.y)}px, 0)` }}
      onKeyDown={toolbarKeys}
    >
      <div className="selection-pill-body">
        <div className="pill-lamp" ref={lamp} aria-hidden="true" />
        {linking ? (
          <LinkField compact />
        ) : (
          <>
            <Tool label="Bold" gesture="bold" kbd={shortcutLabel('Mod-b')} pressed={format.bold} tipSide={pos.below ? 'bottom' : 'top'} onPress={() => run((s) => toggleInline(s, 'bold'))}>
              <BoldIcon size={16} />
            </Tool>
            <Tool label="Italic" gesture="italic" kbd={shortcutLabel('Mod-i')} pressed={format.italic} tipSide={pos.below ? 'bottom' : 'top'} onPress={() => run((s) => toggleInline(s, 'italic'))}>
              <ItalicIcon size={16} />
            </Tool>
            <Tool label="Strikethrough" gesture="strike" kbd={shortcutLabel('Mod-Shift-x')} pressed={format.strike} tipSide={pos.below ? 'bottom' : 'top'} onPress={() => run((s) => toggleInline(s, 'strike'))}>
              <StrikeIcon size={16} />
            </Tool>
            <Tool label="Code" gesture="code" kbd={shortcutLabel('Mod-e')} pressed={format.code} tipSide={pos.below ? 'bottom' : 'top'} onPress={() => run((s) => toggleInline(s, 'code'))}>
              <CodeIcon size={16} />
            </Tool>
            <Tool label="Link" gesture="link" kbd={shortcutLabel('Mod-k')} pressed={format.link} tipSide={pos.below ? 'bottom' : 'top'} onPress={() => patchUi({ linkEditor: 'context' })}>
              <LinkIcon size={16} />
            </Tool>
          </>
        )}
      </div>
    </div>
  );
}
